<script setup lang="ts">
import type { HTMLAttributes } from "vue"
import { computed } from "vue"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { cn } from "@/lib/utils"

export type VoiceAvatarStatus = "ONLINE" | "IDLE" | "DO_NOT_DISTURB" | "INVISIBLE" | "OFFLINE"

const props = withDefaults(defineProps<{
  src?: string | null
  alt?: string
  fallback?: string
  size?: "default" | "sm" | "smplus" | "lg"
  class?: HTMLAttributes["class"]
  fallbackClass?: HTMLAttributes["class"]
  /** Presence status to render as a colored dot. Ignored unless `showStatus` is true. */
  status?: VoiceAvatarStatus | null
  /** Whether to render the presence dot at all — off by default since most avatars (other users, groups) have no known status. */
  showStatus?: boolean
  statusClass?: HTMLAttributes["class"]
}>(), {
  showStatus: false,
  size: "default",
})

const DOT_SIZE_CLASSES: Record<NonNullable<typeof props.size>, string> = {
  sm: "size-4",
  smplus: "size-3",
  default: "size-4",
  lg: "size-5",
}

const STATUS_COLOR_CLASSES: Record<VoiceAvatarStatus, string> = {
  ONLINE: "bg-emerald-500",
  IDLE: "bg-amber-500",
  DO_NOT_DISTURB: "bg-red-500",
  INVISIBLE: "bg-muted-foreground/60",
  OFFLINE: "bg-muted-foreground/60",
}

const statusColorClass = computed(() => STATUS_COLOR_CLASSES[props.status ?? "OFFLINE"])
</script>

<template>
  <div class="relative inline-flex shrink-0">
    <!-- `:key` on the root is what keeps the fallback from disappearing when `src` goes away.
         reka-ui's `AvatarImage` pushes its load status into the shared `AvatarRoot` context and
         never resets it on unmount (`useImageLoadingStatus`'s `onUnmounted` only clears its own
         local flag), while `AvatarFallback` renders only while that shared status is *not*
         "loaded". So on a root instance that Vue reuses across different data — `src` going from a
         URL that finished loading to empty — `v-if="src"` removes the image and the stale "loaded"
         simultaneously suppresses the fallback, leaving a blank circle: the avatar span renders
         with nothing inside it but the two placeholder comments Vue leaves for an unmet `v-if`.
         Keyed on the *presence* of `src`, not its value, so swapping one URL for another (e.g.
         `resolveMediaUrl` upgrading a remote URL to its cached one) doesn't remount and re-flash
         the image — only appearing/disappearing does, which is exactly when the context is stale. -->
    <Avatar :key="src ? 'with-src' : 'no-src'" :size="size" :class="props.class">
      <AvatarImage v-if="src" :src="src" :alt="alt" class="rounded-full" />
      <AvatarFallback :class="fallbackClass">
        <slot name="fallback">{{ fallback }}</slot>
      </AvatarFallback>
    </Avatar>
    <span
        v-if="showStatus"
        :class="cn(
        'absolute right-0 bottom-0 rounded-full border-2 border-background',
        DOT_SIZE_CLASSES[size],
        statusColorClass,
        statusClass,
      )"
    />
  </div>
</template>
